import { useEffect, useState, useRef } from "react";
import { useInView, motion } from "framer-motion";
import "./Careers.scss";
import { toast } from "react-toastify";
import backgroundImage from "../../Assets/images/img17.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSoccerBall,
  faPerson,
  faBriefcase,
  faGift,
  faBarChart,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const Careers = () => {
  const titleRef = useRef(null);
  const isInView = useInView(titleRef, {
    once: true,
    amount: 0.1,
  });

  const openingsSectionRef = useRef(null);

  const [announcements, setAnnouncements] = useState([]);
  const [openings, setOpenings] = useState([]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events`);
        if (response.ok) {
          const data = await response.json();
          setAnnouncements(data);
        } else {
          toast.error("Failed to fetch events.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching events.");
      }
    };

    const fetchJobs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs`);
        if (response.ok) {
          const data = await response.json();
          const visibleOpenings = data.filter((job) => !job.hide);
          setOpenings(visibleOpenings);
        } else {
          toast.error("Failed to fetch job openings.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching job openings.");
      }
    };

    fetchAnnouncements();
    fetchJobs();
  }, []);

  const handleViewOpeningsClick = () => {
    openingsSectionRef.current.scrollIntoView({
      behavior: "smooth",
    });
  };

  const handleApplyNowClick = (jobId) => {
    window.location.href = `careers/job/${jobId}`;
  };

  const iconSize = "2x";

  return (
    <section className="career-page">
      <div
        className="career-banner"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="career-overlay" />
        <div className="career-text container text-left">
          <motion.div
            ref={titleRef}
            initial={{ opacity: 0, y: 100 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
          >
            <h5>CAREERS</h5>
          </motion.div>
        </div>
      </div>

      <div className="white-container">
        <div className="inner-container"></div>
      </div>

      <div className="career-page-content container">
        <div className="career-title-sec">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4>Make your next career move</h4>
          </motion.div>

          <div className="career-form-about">
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.99, ease: "easeOut", delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h5>
                We're seeking passionate individuals ready to make an impact and
                grow with us. Join our team and shape the future!
              </h5>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.99, ease: "easeOut", delay: 0.6 }}
              viewport={{ once: true }}
            >
              <button className="project-btn" onClick={handleViewOpeningsClick}>
                Openings
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  height="15px"
                  width="15px"
                  className="project-icon"
                >
                  <path
                    stroke-linejoin="round"
                    stroke-linecap="round"
                    stroke-miterlimit="10"
                    stroke-width="1.5"
                    stroke="#292D32"
                    d="M8.91016 19.9201L15.4302 13.4001C16.2002 12.6301 16.2002 11.3701 15.4302 10.6001L8.91016 4.08008"
                  ></path>
                </svg>
              </button>
            </motion.div>
          </div>
        </div>

        <div className="perks-section container">
          <div className="perks-title row">
            <div className="col-md-12">
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h2 className="perks-title">Why Work With Us?</h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h5 className="perks-subtitle">
                  Elevating experiences and exclusive benefits await you here
                </h5>
              </motion.div>
            </div>
          </div>

          <div className="row">
            <div className="perks-content col-md-3">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="logo-container">
                  <FontAwesomeIcon icon={faSoccerBall} size={iconSize} />
                </div>
                <p className="perks-text">
                  Enjoy five workdays, weekends for quality time, and monthly
                  sports activities, balancing productivity with relaxation and
                  recreation.
                </p>
              </motion.div>
            </div>

            <div className="perks-content col-md-3">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="logo-container">
                  <FontAwesomeIcon icon={faPerson} size={iconSize} />
                </div>
                <p className="perks-text">
                  Our culture values dedication and excellence, offering you the
                  chance to step into leadership roles where your skills can
                  shine.
                </p>
              </motion.div>
            </div>

            <div className="perks-content col-md-3">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="logo-container">
                  <FontAwesomeIcon icon={faGift} size={iconSize} />
                </div>
                <p className="perks-text">
                  Hard work is rewarded here with appreciation and bonuses,
                  recognizing both individual achievements and team
                  contributions.
                </p>
              </motion.div>
            </div>

            <div className="perks-content col-md-3">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="logo-container">
                  <FontAwesomeIcon icon={faBarChart} size={iconSize} />
                </div>
                <p className="perks-text">
                  Aron fosters growth, turning every project into an opportunity
                  to enhance skills, advance professionally, and achieve your
                  career goals.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="openings-section" ref={openingsSectionRef}>
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <motion.div
                  initial={{ opacity: 0, x: -100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <h2 className="openings-title">Current Openings</h2>
                </motion.div>
              </div>
            </div>

            <div className="row">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true }}
              >
                {openings.length > 0 ? (
                  openings.map((opening) => (
                    <div className="col-md-12" key={opening.id}>
                      <div className="openings-column">
                        <div className="first-part">
                          <div className="opening-img-div">
                            <img
                              className="briefcase-img"
                              src="https://www.lftechnology.com/images/icons/briefcase_outline.svg"
                              alt="briefcase"
                            />
                          </div>
                          <div className="openings-texts">
                            <h2 className="openings-name">{opening.title}</h2>
                          </div>
                        </div>

                        <div className="last-part">
                          <h2
                            className="joinnow"
                            onClick={() => handleApplyNowClick(opening.id)}
                          >
                            Apply Now
                          </h2>
                          <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">
                    <p className="no-openings-text">
                      🚫 No openings available for now. Please check back later!
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Careers;
